// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Amplify } from 'aws-amplify';

// Layout Components
import TopBar from './components/layout/TopBar';
import Footer from './components/layout/Footer';
import DisclaimerModal from './components/layout/DisclaimerModal';

// Pages
import Home from './pages/Home';
import ServicePage from './pages/ServicePage'; // Single service page component
import ServicesListingPage from './pages/ServicesListingPage'; 
import TeamMember from './pages/TeamMember';
import TeamPage from './pages/TeamPage';
import AIServices from './pages/AIServices';
import ProBonoPage from './pages/ProBonoPage';
import ValuesPage from './pages/ValuesPage';
import CareersPage from './pages/CareersPage';
import FirmPage from './pages/FirmPage';
import ContactPage from './pages/ContactPage';
import BlogsPage from './pages/BlogsPage';
import BlogDetailPage from './pages/BlogDetailPage';
import NotFound from './pages/NotFound';

function App() {
  // State to manage the disclaimer visibility
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [amplifyConfigured, setAmplifyConfigured] = useState(false);
  
  // Configure Amplify when the app starts using environment variables
  useEffect(() => {
    const configureAmplify = async () => {
      try {
        // Set default values if environment variables aren't set
        const config = {
          aws_project_region: process.env.REACT_APP_AWS_REGION || 'ap-south-1',
          aws_cognito_identity_pool_id: process.env.REACT_APP_IDENTITY_POOL_ID || 'ap-south-1:b1809c99-2e4a-4038-a14c-534f3718af44',
          aws_cognito_region: process.env.REACT_APP_AWS_REGION || 'ap-south-1',
          aws_user_pools_id: process.env.REACT_APP_USER_POOL_ID || 'ap-south-1_DmlRq48YI',
          aws_user_pools_web_client_id: process.env.REACT_APP_CLIENT_ID || '46ppupqs5eg9sstni3ni1s1rf1',
          aws_user_files_s3_bucket: process.env.REACT_APP_S3_BUCKET || 'amvlawwebsitefa905-dev',
          aws_user_files_s3_bucket_region: process.env.REACT_APP_AWS_REGION || 'ap-south-1'
        };
        
        Amplify.configure(config);
        setAmplifyConfigured(true);
      } catch (error) {
        console.error('Error configuring Amplify:', error);
      }
    };
    
    configureAmplify();
  }, []);
  
  // Check local storage for disclaimer acceptance on component mount
  useEffect(() => {
    const hasAccepted = localStorage.getItem('disclaimerAccepted');
    if (hasAccepted === 'true') {
      setDisclaimerAccepted(true);
    }
  }, []);
  
  // Handle disclaimer acceptance
  const handleDisclaimerAgree = () => {
    setDisclaimerAccepted(true);
    localStorage.setItem('disclaimerAccepted', 'true');
  };
  
  // Function to show disclaimer again (for footer links)
  const handleShowDisclaimer = () => {
    setDisclaimerAccepted(false);
  };

  return (
    <Router>
      <div className="min-h-screen bg-white flex flex-col">
        {/* Disclaimer modal with conditional rendering */}
        <DisclaimerModal isOpen={!disclaimerAccepted} onAgree={handleDisclaimerAgree} />
        
        <TopBar />
        
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            
            {/* Services Routes */}
            <Route path="/services" element={<ServicesListingPage />} />
            <Route path="/services/:id" element={<ServicePage />} />
            
            {/* Team Routes */}
            <Route path="/team" element={<TeamPage />} />
            <Route path="/team/:id" element={<TeamMember />} />
            
            {/* About Section Routes */}
            <Route path="/about/firm" element={<FirmPage />} />
            <Route path="/about/values" element={<ValuesPage />} />
            <Route path="/about/team" element={<TeamPage />} />
            <Route path="/about/careers" element={<CareersPage />} />
            
            {/* Blog Routes */}
            <Route path="/blogs" element={<BlogsPage />} />
            <Route path="/blogs/:id" element={<BlogDetailPage />} />
            
            {/* Also keep the direct routes for backward compatibility */}
            <Route path="/firm" element={<FirmPage />} />
            <Route path="/values" element={<ValuesPage />} />
            <Route path="/careers" element={<CareersPage />} />
            
            {/* Other Routes */}
            <Route path="/ai-services" element={<AIServices />} />
            <Route path="/pro-bono" element={<ProBonoPage />} />
            <Route path="/contact" element={<ContactPage />} />
            
            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        
        <Footer onDisclaimerClick={handleShowDisclaimer} />
      </div>
    </Router>
  );
}

export default App;